import Button from '@material-ui/core/Button';
import FormControl from '@material-ui/core/FormControl';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { darken } from '@material-ui/core/styles/colorManipulator';
import Tab from '@material-ui/core/Tab';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableRow from '@material-ui/core/TableRow';
import Tabs from '@material-ui/core/Tabs';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import DeleteIcon from '@material-ui/icons/Close';
import Autocomplete from '@material-ui/lab/Autocomplete';
import React, { useState } from 'react';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    panel: {
      padding: 24,
      backgroundColor: theme.palette.background.paper,
      boxShadow: '0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)'
    },
    addPermissionPanel: {
      padding: 16,
      backgroundColor: darken(theme.palette.background.default, 0.02),
      display: 'flex'
    },
    addPermissionGridContainer: { 
      flex: 1, 
      marginRight: 16 
    },
    topSection: { 
      marginBottom: 24 
    },
    tabs: { 
      marginBottom: 24 
    },
    removePermissionTableCell: { 
      width: 50 
    }
  })
);

interface Props {
  workflowAccess: any;
  groups: any;
  onWorkflowAccessChange: (workflowAccess) => void;
}

function WorkflowAccessPermissionsEditor(props: Props) {
  const { workflowAccess, groups, onWorkflowAccessChange } = props;
  const [groupToAdd, setGroupToAdd] = useState(null);
  const [serviceArnToAdd, setServiceArnToAdd] = useState('');
  const [tab, setTab] = useState('group');
  const [selectedRole, setSelectedRole] = useState('editor');
  const [permissions, setPermissions] = useState([
    ...workflowAccess.WorkflowOwner ? [{ id: workflowAccess.WorkflowOwner, name: workflowAccess.WorkflowOwner, type: 'owner' }] : [],
    ...workflowAccess.WorkflowRunners ? workflowAccess.WorkflowRunners.map(group => ({ id: group, name: groups[group], type: 'runner' })) : [],
    ...workflowAccess.WorkflowEditors ? workflowAccess.WorkflowEditors.map(group => ({ id: group, name: groups[group], type: 'editor' })) : [],
    ...workflowAccess.AllowedARNs ? workflowAccess.AllowedARNs.map(arn => ({ id: arn, name: arn, type: 'arn' })) : []
  ]);

  const classes = useStyles({});

  const updatePermissions = (newPermissions: any[]) => {
    setPermissions(newPermissions);
    onWorkflowAccessChange(newPermissions.reduce((accumulator, permission) => {
      if (permission.type === 'editor') {
        accumulator.WorkflowEditors.push(permission.id);
      } else if (permission.type === 'runner') {
        accumulator.WorkflowRunners.push(permission.id);
      } else if (permission.type === 'arn') {
        accumulator.AllowedARNs.push(permission.id);
      } else {
        accumulator.WorkflowOwner = permission.id;
      }
      return accumulator;
    }, {
      WorkflowEditors: [],
      WorkflowRunners: [],
      AllowedARNs: []
    }));
  };

  const onGroupChange = (event: React.ChangeEvent<{}>, value: any) => {
    setGroupToAdd(value);
  };

  const onSelectedRoleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setSelectedRole(event.target.value as string);
  };

  const onAddGroup = () => {
    const permissionsCopy = permissions.slice();
    permissionsCopy.push({ id: groupToAdd.id, name: groupToAdd.name, type: selectedRole });
    updatePermissions(permissionsCopy);
    setGroupToAdd(null);
    setSelectedRole('editor');
  };

  const onAddServiceArn = () => {
    // Check if arn already exists
    if (!permissions.some(permission => permission.type === 'arn' && permission.id === serviceArnToAdd)) {
      const permissionsCopy = permissions.slice();
      permissionsCopy.push({ id: serviceArnToAdd, name: serviceArnToAdd, type: 'arn' });
      updatePermissions(permissionsCopy);
    }
    setServiceArnToAdd('');
  };

  const onRemovePermission = (permission) => () => {
    const permissionsCopy = permissions.slice();
    permissionsCopy.splice(permissionsCopy.indexOf(permission), 1);
    updatePermissions(permissionsCopy);
  };

  const onEditPermission = (permission) => (event: React.ChangeEvent<{ value: unknown }>) => {
    const permissionsCopy = permissions.slice();
    const index = permissionsCopy.indexOf(permission);
    permissionsCopy[index] = { ...permission, type: event.target.value as string };
    updatePermissions(permissionsCopy);
  };

  const onTabChange = (event: React.ChangeEvent<{}>, newValue: string) => {
    setTab(newValue);
  }

  const onServiceArnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setServiceArnToAdd(event.target.value);
  };

  const selectedGroupsSet = permissions.reduce((accumulator, item) => {
    if (item.type === 'runner' || item.type === 'editor') {
      accumulator.add(item.id);
    }
    return accumulator;
  }, new Set());

  const availableGroups = Object.keys(groups).filter(key => !selectedGroupsSet.has(key)).map(key => ({ id: key, name: groups[key] }));

  return (
    <div className={classes.panel}>
      <div className={classes.topSection}>
        <Tabs
          className={classes.tabs}
          value={tab}
          onChange={onTabChange}
          aria-label='Workflow Details Tabs'
          indicatorColor='primary'
          textColor='primary'
        >
          <Tab label='Add Group' value='group' />
          <Tab label='Add Service ARN' value='service' />
        </Tabs>
        {tab === 'group' && <div className={classes.addPermissionPanel}>
          <Grid container spacing={4} className={classes.addPermissionGridContainer}>
            <Grid item xs={8}>
              <Autocomplete
                value={groupToAdd}
                onChange={onGroupChange}
                options={availableGroups}
                getOptionLabel={(option) => option.name}
                getOptionSelected={(option, value) => value ? option.id === value.id : false}
                renderInput={(params) => <TextField {...params} label='Groups' placeholder='Select a group to add' InputLabelProps={{
                  shrink: true
                }} />}
              />
            </Grid>
            <Grid item xs>
              <FormControl fullWidth>
                <InputLabel id="group-role-selection">Role</InputLabel>
                <Select
                  id="group-role-selection"
                  value={selectedRole}
                  fullWidth
                  onChange={onSelectedRoleChange}
                >
                  <MenuItem value="editor">Editor</MenuItem>
                  <MenuItem value="runner">Runner</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <Button
            disabled={!groupToAdd}
            color={!groupToAdd ? 'default' : 'primary'}
            variant='outlined'
            onClick={onAddGroup}
          >
            Add
          </Button>
        </div>}
        {tab === 'service' && <div className={classes.addPermissionPanel}>
          <Grid container spacing={4} className={classes.addPermissionGridContainer}>
            <Grid item xs={8}>
              <TextField
                label='Service ARN'
                value={serviceArnToAdd}
                onChange={onServiceArnChange}
                fullWidth
                placeholder="ARN of service or IAM user"
                InputLabelProps={{
                  shrink: true
                }} />
            </Grid>
            <Grid item xs>
              <FormControl fullWidth>
                <InputLabel id="service-role-selection">Role</InputLabel>
                <Select
                  id="service-role-selection"
                  value={'runner'}
                  fullWidth
                  disabled
                >
                  <MenuItem value="runner">Runner</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <Button
            disabled={!serviceArnToAdd}
            color={!serviceArnToAdd ? 'default' : 'primary'}
            variant='outlined'
            onClick={onAddServiceArn}
          >
            Add
          </Button>
        </div>}
      </div>
      <div>
        <TableContainer>
          <Table size="small">
            <TableBody>

              {permissions.map(permission => <TableRow key={permission.id}>
                <TableCell>
                  <Typography>{permission.name} {permission.type === 'editor' || permission.type === 'runner' ? `(${permission.id})` : ''}</Typography>
                </TableCell>
                <TableCell>
                  {(permission.type === 'editor' || permission.type === 'runner') && <Select
                    value={permission.type}
                    fullWidth
                    onChange={onEditPermission(permission)}
                  >
                    <MenuItem value="editor">Editor</MenuItem>
                    <MenuItem value="runner">Runner</MenuItem>
                  </Select>}
                  {permission.type === 'arn' && <Select
                    value={'runner'}
                    fullWidth
                    disabled
                  >
                    <MenuItem value="runner">Runner</MenuItem>
                  </Select>}
                  {permission.type === 'owner' && <Select
                    value={'owner'}
                    fullWidth
                    disabled
                  >
                    <MenuItem value="owner">Owner</MenuItem>
                  </Select>}
                </TableCell>
                <TableCell align='right' className={classes.removePermissionTableCell}>
                  {permission.type !== 'owner' && <IconButton onClick={onRemovePermission(permission)}><DeleteIcon /></IconButton>}
                </TableCell>
              </TableRow>)}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    </div>);
};

export default WorkflowAccessPermissionsEditor;
